<!--
  The reading-direction task's work surface: one photograph, two judgements, no canvas.

  Deliberately not the Konva workspace. This task is a look and a choice — which way does this
  run, and is it linear, boustrophedon or spiral — and loading a box editor to answer it would
  put a drawing tool in the hands of somebody who came to read a layout. A plain `<img>` at the
  presigned URL is enough to see a baseline.

  It saves through `PATCH /api/inscriptions/by-id/{id}`. The direction is a fact about the
  writing, so one save settles every photograph of the stone — which is why answering it here
  finishes the record rather than one of its images. Never the bulk upsert: that payload's box
  list is authoritative, and it can no longer carry a direction at all.

  It also does **not** take the annotation lock. A fifteen-minute claim for a one-field write
  would lock a boxer out of an image somebody glanced at. It still refuses when somebody else
  holds a live one on *any* of the record's images — the value is shared, so the risk is —
  which the API answers with a 409 and this screen shows.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import { api } from '../lib/api/client';
  import { asProblem, type ApiProblem } from '../lib/api/problem';
  import type {
    ImageDetail,
    InscriptionDetail,
    LayoutType,
    ReadingDirection,
  } from '../lib/api/types';
  import { directionLabel } from '../lib/draft';
  import { href, router } from '../lib/router.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import ProgressMeter from './ProgressMeter.svelte';

  interface Props {
    imageId: number;
  }

  const { imageId }: Props = $props();

  const TASK_ID = 'reading-direction';
  /**
   * `angle` is no longer offered.
   *
   * The schema still accepts it and the service still stores it, so a record already carrying it
   * keeps its answer and its degrees: the option is put back **only** for that record, so the
   * select never reads "not set" over a stored direction. Nothing is offered it fresh.
   */
  const DIRECTIONS: ReadingDirection[] = ['ltr', 'rtl', 'ttb', 'btt'];
  const LAYOUTS: LayoutType[] = ['linear', 'boustrophedon', 'spiral'];

  let image = $state<ImageDetail | null>(null);
  let inscription = $state<InscriptionDetail | null>(null);
  let imageUrl = $state<string | null>(null);
  let loading = $state(true);
  let saving = $state(false);
  let problem = $state<ApiProblem | null>(null);
  let saved = $state(false);

  // The editable copy. Separate from `inscription` so an unsaved choice is visibly unsaved.
  let direction = $state<ReadingDirection | null>(null);
  let angle = $state<number | null>(null);
  let layout = $state<LayoutType | null>(null);

  const dirty = $derived(
    inscription !== null &&
      (direction !== inscription.reading_direction ||
        angle !== inscription.reading_direction_angle ||
        layout !== inscription.layout_type),
  );

  /**
   * An image with no linked record has nowhere to store an answer.
   *
   * The direction is the inscription's, and the catalog could not join this photograph to one
   * — it is an orphan, or its siglum is shared by two records. Such images are already outside
   * this task's scope, so nobody reaches this screen by working the queue; it is reachable by
   * URL, and saying so beats a control that accepts a judgement and discards it.
   */
  const editable = $derived(annotator.ready && inscription !== null);

  const savable = $derived(
    dirty && editable && !saving && !(direction === 'angle' && angle === null),
  );

  const directions = $derived(
    direction === 'angle' ? [...DIRECTIONS, 'angle' as const] : DIRECTIONS,
  );

  $effect(() => {
    const id = imageId;
    let cancelled = false;
    untrack(() => {
      void queue.ensureCards();
      void (async () => {
        loading = true;
        problem = null;
        try {
          const state = await api.imageAnnotations(id);
          if (cancelled) return;
          image = state.image;
          // The presigned URL is minted per request and never stored. It is not cached here
          // either: this component is remounted per image (`{#key}` in App.svelte), so the URL
          // lives exactly as long as the view that uses it.
          const [url, record] = await Promise.all([
            api.imageUrl(id),
            state.image.inscription_id !== null
              ? api.inscriptionById(state.image.inscription_id)
              : Promise.resolve(null),
          ]);
          if (cancelled) return;
          imageUrl = url.url;
          if (record) apply(record);
        } catch (error) {
          if (!cancelled) problem = asProblem(error);
        } finally {
          if (!cancelled) loading = false;
        }
      })();
    });
    return () => {
      cancelled = true;
    };
  });

  function apply(detail: InscriptionDetail): void {
    inscription = detail;
    direction = detail.reading_direction;
    angle = detail.reading_direction_angle;
    layout = detail.layout_type;
  }

  async function save(): Promise<void> {
    if (!savable || !inscription) return;
    saving = true;
    problem = null;
    saved = false;
    try {
      // Direction and angle always travel together: `angle` must carry degrees and no other
      // direction may, so sending one without the other is a 422 by construction.
      apply(
        await api.patchInscription(inscription.id, {
          reading_direction: direction,
          reading_direction_angle: direction === 'angle' ? angle : null,
          layout_type: layout,
        }),
      );
      saved = true;
      void queue.load();
    } catch (error) {
      problem = asProblem(error);
    } finally {
      saving = false;
    }
  }

  /**
   * The next record needing a direction.
   *
   * No sibling hunt any more. It used to look for another photograph of this stone still
   * missing a direction, because the answer was stored per image; one save now settles all of
   * them, so the record is finished and the next thing is the next record.
   */
  async function advance(): Promise<void> {
    const record = inscription;
    if (record) {
      const next = queue.nextAfter(record.id);
      if (next?.next_image_id != null) {
        router.go({ name: 'direction', imageId: next.next_image_id });
        return;
      }
    }
    router.go({ name: 'queue', task: TASK_ID });
  }

  async function saveAndAdvance(): Promise<void> {
    if (dirty) await save();
    if (!problem) await advance();
  }

  const card = $derived(queue.cards?.find((entry) => entry.id === TASK_ID) ?? null);
  const meter = $derived(card?.progress.meters.find((entry) => entry.key === 'direction') ?? null);
</script>

<div class="direction col">
  <div class="row wrap head">
    <div class="col" style="gap:2px">
      <h2>{inscription?.siglum_raw ?? `Image #${imageId}`}</h2>
      {#if inscription?.ociana_id}
        <span class="small muted mono">{inscription.ociana_id}</span>
      {/if}
    </div>
    <span class="spacer"></span>
    <a class="small" href={href({ name: 'queue', task: TASK_ID })}>← Direction queue</a>
    {#if image}
      <a class="small" href={href({ name: 'image', imageId })}>Open in the box editor</a>
    {/if}
  </div>

  {#if problem}
    <div class="banner error small">
      <strong>{problem.code === 'image_locked' ? 'Somebody else has this image' : 'Failed'}</strong>
      <p>{problem.message}</p>
      <p class="muted mono">code: {problem.code}</p>
    </div>
  {/if}

  {#if annotator.readOnlyAccount}
    <p class="banner warn small">
      Your account is a <strong>viewer</strong>, so nothing here can be saved.
    </p>
  {/if}

  <div class="split">
    <div class="panel col">
      {#if loading}
        <p class="small muted">Loading the photograph…</p>
      {:else if imageUrl}
        <img src={imageUrl} alt={`Inscription ${inscription?.siglum_raw ?? imageId}`} />
      {:else}
        <p class="small muted">No imagery is available for this record.</p>
      {/if}
    </div>

    <div class="col side">
      <div class="panel col">
        <h3>Which way does it run?</h3>

        {#if !loading && inscription === null}
          <p class="small warn-text">
            This photograph matches no record, so there is no inscription to carry a reading
            direction — it is an orphan, or its siglum is shared by two records. Nothing here
            can be saved.
          </p>
        {/if}

        <label class="col small">
          Reading direction
          <select
            value={direction ?? ''}
            disabled={!editable}
            onchange={(event) => {
              direction = (event.currentTarget.value || null) as ReadingDirection | null;
              if (direction !== 'angle') angle = null;
            }}
          >
            <option value="">not set</option>
            {#each directions as option (option)}
              <option value={option}>{option} — {directionLabel(option)}</option>
            {/each}
          </select>
        </label>

        {#if direction === 'angle'}
          <label class="col small">
            Baseline angle (degrees)
            <input
              type="number"
              step="0.5"
              value={angle ?? ''}
              disabled={!editable}
              onchange={(event) => {
                angle = event.currentTarget.value ? Number(event.currentTarget.value) : null;
              }}
            />
            {#if angle === null}
              <span class="small warn-text">
                An angled baseline has to carry its degrees, or it says nothing.
              </span>
            {/if}
          </label>
        {/if}

        <label class="col small">
          Layout
          <select
            value={layout ?? ''}
            disabled={!editable}
            onchange={(event) => {
              layout = (event.currentTarget.value || null) as LayoutType | null;
            }}
          >
            <option value="">not set</option>
            {#each LAYOUTS as option (option)}
              <option value={option}>{option}</option>
            {/each}
          </select>
        </label>

        <p class="small muted">
          One answer per stone: every photograph of this inscription gets it, so the record is
          finished when you save. It turns the reference forms in the box editor's chip strip
          and lays the strip out the record's way; it decides <strong>nothing a box stores</strong>,
          so a wrong answer here is a wrong caption rather than a wrong label. Leave the layout
          unset rather than guessing it; an unset layout is a fact, and a wrong one is not.
        </p>

        <div class="row">
          <button onclick={() => void save()} disabled={!savable}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onclick={() => void saveAndAdvance()} disabled={!editable || saving}>
            Save and next
          </button>
          <span class="spacer"></span>
          {#if dirty}
            <span class="small warn-text">unsaved</span>
          {:else if saved}
            <span class="small muted">saved</span>
          {/if}
        </div>
      </div>

      {#if inscription}
        <div class="panel col">
          <h3>What OCIANA reads</h3>
          <p class="translit">{inscription.translit ?? '—'}</p>
          {#if inscription.translation}
            <p class="small muted">{inscription.translation}</p>
          {/if}
          <p class="small muted">
            A weak label: it tells you which letters and in what order, not which way they run.
            Where it disagrees with the stone, the stone is what you are recording.
          </p>
        </div>
      {/if}

      {#if meter}
        <div class="panel col">
          <ProgressMeter
            label={meter.label}
            value={meter.value}
            total={meter.total}
            detail={meter.detail}
            tone={meter.total > 0 && meter.value >= meter.total ? 'ok' : 'accent'}
          />
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .direction {
    padding: 12px;
    gap: 12px;
    /* No `max-width`. The task is *look at the photograph and say which way it runs*; capping
       the page at 1400px on a wider monitor bought nothing and cost the picture the difference.
       The controls column is capped instead, just below. */
    margin: 0 auto;
    width: 100%;
  }

  .head {
    align-items: flex-start;
  }

  .split {
    display: grid;
    /*
      The photograph takes the room, and the controls take what they need.

      This was `2fr / minmax(300px, 1fr)`, which gave a third of a 1400px page to a panel
      holding two `<select>`s — so on a 1512px monitor the image rendered about 540px wide with
      600px of empty space beside it, for a judgement you make by *looking*. A fixed, capped
      control column and `1fr` for the image reverses that: everything the window gains from
      here goes to the picture.
    */
    grid-template-columns: minmax(0, 1fr) clamp(300px, 24vw, 380px);
    gap: 12px;
    align-items: start;
  }

  @media (max-width: 900px) {
    .split {
      grid-template-columns: 1fr;
    }
  }

  .side {
    gap: 12px;
  }

  img {
    width: 100%;
    height: auto;
    /* Was 72vh. The screen has nothing else competing for the height — one heading, two
       selects and a meter, all in the column beside it. */
    max-height: 82vh;
    object-fit: contain;
    background: var(--bg-input);
    border: 1px solid var(--line);
    border-radius: 4px;
  }

  .translit {
    font-size: 1.1rem;
    margin: 0;
  }

  .warn-text {
    color: var(--warn);
  }
</style>
